import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  BarChart3, TrendingUp, TrendingDown, Target, DollarSign, Coins, Users, 
  ArrowRight, ArrowUp, ArrowDown, Bell, Settings, Search, Menu,
  ChevronDown, ChevronUp, Calendar, Download, RefreshCw, Zap, AlertTriangle,
  FileText, Loader2, LogOut, Globe, Building2, FileBarChart2,
  Cpu, UserCheck, Wrench, ChevronRight, CheckCircle2, X, MessageSquare,
  Send, Paperclip, Bot, Sparkles, Upload, Shield, Brain
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import FreeTrialModal from '../components/modals/FreeTrialModal';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';
import axios from 'axios';
import { getCurSym, hasPeerValue, hasMetric, metricText, impactText, metricComparison, scorecardStats } from '../utils/scorecard';
import { useProposal } from '../hooks/useProposal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hidden globally at the owner's request; preserve saved data and other reports.
const COMPANY_DASHBOARD_TOOLS_ENABLED = false;

// Helper to format units - converts 'percentage', 'Percentage', 'percent' to '%'
// Helper to format units - converts 'percentage' to '%' and prefixes a space for readability
const formatUnit = (unit) => {
  if (!unit) return '';
  const lowerUnit = unit.toLowerCase();
  if (lowerUnit === 'percentage' || lowerUnit === 'percent') return '%';
  if (unit === '%' || unit === 'x') return unit;
  return ` ${unit}`;
};

// Collapsible Section Component for mobile
const CollapsibleCard = ({ title, icon: Icon, children, defaultOpen = false, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const sectionId = `dashboard-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <button
        data-testid={`${sectionId}-toggle`}
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 sm:p-6 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-emerald-600" />}
          <h2 className="text-base sm:text-lg font-bold text-gray-900">{title}</h2>
          {badge && (
            <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      {isOpen && <div className="border-t border-gray-100" data-testid={`${sectionId}-content`}>{children}</div>}
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('id');
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingFromUrl, setLoadingFromUrl] = useState(false);
  const [error, setError] = useState('');
  const [showTrialModal, setShowTrialModal] = useState(false);
  
  // User and analysis data
  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  
  // Secret code verification state
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [secretCode, setSecretCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  
  // Proposal state
  const { proposal, proposalLoading, proposalError, fetchOrGenerateProposal } = useProposal(userId, COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'proposal');
  
  // Agentic AI Chatbot state
  const [agenticPrompt, setAgenticPrompt] = useState('');
  const [agenticFile, setAgenticFile] = useState(null);
  const [agenticLoading, setAgenticLoading] = useState(false);
  const [agenticProposal, setAgenticProposal] = useState(null);
  const [agenticHistory, setAgenticHistory] = useState([]);
  const [agenticError, setAgenticError] = useState('');
  const fileInputRef = useRef(null);
  
  // Expansion states for areas of improvement
  const [expandedAreas, setExpandedAreas] = useState({});

  // Check if user has full access
  const checkAccess = async (uid) => {
    try {
      const response = await axios.get(`${API}/check-access/${uid}`);
      setHasFullAccess(response.data.has_access);
    } catch (err) {
      console.error('Failed to check access:', err);
      setHasFullAccess(false);
    }
  };

  // Verify secret code
  const handleVerifyCode = async () => {
    if (!secretCode || secretCode.length !== 6) {
      setCodeError('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    setCodeError('');
    
    try {
      const response = await axios.post(`${API}/verify-secret-code`, {
        user_id: userId,
        secret_code: secretCode
      });
      
      if (response.data.verified) {
        if (response.data.access_token) {
          localStorage.setItem(`vsc_token_${userId}`, response.data.access_token);
        }
        setHasFullAccess(true);
        // Refetch analysis with the issued access token to get complete data
        await fetchAnalysis(userId);
      }
    } catch (err) {
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Invalid secret code';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || errorMessage;
      }
      
      setCodeError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  useEffect(() => {
    // First check for URL parameter
    if (userIdFromUrl) {
      loadFromUrl(userIdFromUrl);
      return;
    }
    
    // Otherwise check localStorage
    const storedUserId = localStorage.getItem('samanvai_user_id');
    const storedUserName = localStorage.getItem('samanvai_user_name');
    const storedCompany = localStorage.getItem('samanvai_company');
    
    if (!storedUserId) {
      setShowTrialModal(true);
      setLoading(false);
      return;
    }
    
    setUserId(storedUserId);
    setUserName(storedUserName || 'User');
    setCompanyName(storedCompany || 'Your Company');
    
    // Check access status and then fetch analysis (server enforces the gate via token)
    const loadWithAccessCheck = async () => {
      try {
        await fetchAnalysis(storedUserId);
      } catch (err) {
        console.error('Failed to load analysis:', err);
        setHasFullAccess(false);
      }
    };
    
    loadWithAccessCheck();
  }, [userIdFromUrl]);
  
  // Load analysis from URL parameter
  const loadFromUrl = async (uid) => {
    setLoadingFromUrl(true);
    setLoading(true);
    try {
      // Fetch user info
      const userResponse = await axios.get(`${API}/trial/user/${uid}`);
      if (userResponse.data) {
        setUserId(uid);
        setUserName(userResponse.data.name || 'User');
        setCompanyName(userResponse.data.company_name || 'Your Company');
        
        // Store in localStorage for future visits
        localStorage.setItem('samanvai_user_id', uid);
        localStorage.setItem('samanvai_user_name', userResponse.data.name || 'User');
        localStorage.setItem('samanvai_company', userResponse.data.company_name || 'Your Company');
      }
      
      // Check access status first - CRITICAL: Don't grant access until code is verified
      const accessResponse = await axios.get(`${API}/check-access/${uid}`);
      const userHasAccess = accessResponse.data.has_access === true;
      console.log('Access check result:', { uid, has_access: userHasAccess, response: accessResponse.data });
      
      // Fetch analysis - the server enforces the gate via the stored access token
      await fetchAnalysis(uid);
    } catch (err) {
      console.error('Failed to load from URL:', err);
      // On error, default to showing sample preview (not full access)
      setHasFullAccess(false);
      if (err.response?.status === 404) {
        setError('Assessment not found. Please start a new assessment.');
      } else {
        setError('Failed to load your assessment. Please try again.');
      }
    } finally {
      setLoadingFromUrl(false);
      setLoading(false);
    }
  };

  const fetchAnalysis = async (uid) => {
    try {
      // Server-enforced gate: send the access token issued at code verification
      const tok = localStorage.getItem(`vsc_token_${uid}`);
      console.log('Fetching analysis:', { uid, hasToken: !!tok });
      const response = await axios.get(`${API}/analysis/${uid}`, {
        headers: tok ? { 'X-Access-Token': tok } : {}
      });
      console.log('Analysis response:', { 
        is_sample_preview: response.data.is_sample_preview,
        kpis_count: (response.data.kpis || []).length,
        total_kpis: response.data.total_kpis_count
      });
      setAnalysis(response.data);
      setHasFullAccess(!response.data.is_sample_preview);
    } catch (err) {
      console.error('Failed to fetch analysis:', err);
      if (err.response?.status === 404) {
        // Show error message instead of trial modal when accessing via URL
        if (userIdFromUrl) {
          setError('Assessment not found or has expired. The assessment ID may be from a previous session. Please start a new assessment.');
        } else {
          setShowTrialModal(true);
        }
      } else {
        setError('Failed to load analysis data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('samanvai_user_id');
    localStorage.removeItem('samanvai_user_name');
    localStorage.removeItem('samanvai_company');
    localStorage.removeItem('samanvai_verified');
    navigate('/');
  };

  // Fetch agentic proposal history when switching to agentic tab
  useEffect(() => {
    if (COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'agentic' && userId && agenticHistory.length === 0) {
      fetchAgenticHistory();
    }
  }, [activeTab, userId]);

  // Fetch audit trail when switching to audit tab
  useEffect(() => {
    if (COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'audit' && userId) {
      const tok = localStorage.getItem(`vsc_token_${userId}`);
      setAuditLoading(true);
      axios.get(`${API}/analysis/${userId}/audit-log`, { headers: tok ? { 'X-Access-Token': tok } : {} })
        .then((res) => setAuditLog(res.data.entries || []))
        .catch((err) => { console.error('Failed to fetch audit log:', err); setAuditLog([]); })
        .finally(() => setAuditLoading(false));
    }
  }, [activeTab, userId]);

  const fetchAgenticHistory = async () => {
    if (!COMPANY_DASHBOARD_TOOLS_ENABLED) return;
    try {
      const response = await axios.get(`${API}/agentic/proposals/${userId}`);
      setAgenticHistory(response.data || []);
    } catch (err) {
      console.error('Failed to fetch agentic history:', err);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        alert('File size must be less than 20MB');
        return;
      }
      setAgenticFile(file);
    }
  };

  const handleAgenticSubmit = async () => {
    if (!COMPANY_DASHBOARD_TOOLS_ENABLED) return;
    if (!agenticPrompt.trim()) {
      alert('Please enter your areas of concern or improvement');
      return;
    }

    setAgenticLoading(true);
    setAgenticError('');
    setAgenticProposal(null);

    try {
      const formData = new FormData();
      formData.append('user_id', userId);
      formData.append('prompt', agenticPrompt);
      formData.append('company_name', companyName);
      formData.append('sector', analysis?.sector || 'Industry');
      
      if (agenticFile) {
        formData.append('document', agenticFile);
      }

      const response = await axios.post(`${API}/agentic/generate-proposal`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000 // 3 minutes timeout
      });

      setAgenticProposal(response.data);
      setAgenticHistory(prev => [response.data, ...prev]);
      setAgenticPrompt('');
      setAgenticFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      console.error('Failed to generate agentic proposal:', err);
      setAgenticError('Failed to generate proposal. Please try again.');
    } finally {
      setAgenticLoading(false);
    }
  };

  const toggleAreaExpansion = (index) => {
    setExpandedAreas(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Calculate summary metrics from analysis data
  const kpiData = analysis?.kpis || [];
  const allPeerRows = analysis?.peer_comparison || [];
  const peerComparison = allPeerRows.filter((pc) => [pc.peer1Value, pc.peer2Value, pc.peer3Value].some(hasPeerValue));
  const hiddenPeerRows = allPeerRows.length - peerComparison.length;
  const curSym = getCurSym(analysis);
  const peers = analysis?.peers || [];
  const areasOfImprovement = analysis?.areas_of_improvement || [];
  const fiveYearRoadmap = analysis?.five_year_roadmap || [];
  
  // Helper function to determine if lower is better for a KPI
  const isLowerBetter = (kpiName) => {
    const name = kpiName.toLowerCase();
    return name.includes('cost') || name.includes('churn') || name.includes('turnover') ||
           name.includes('incident') || name.includes('downtime') || name.includes('defect') ||
           name.includes('error') || name.includes('emission') || name.includes('cac') ||
           name.includes('attrition') || name.includes('debt') || name.includes('lead time');
  };
  
  const { kpisAboveAvg, kpisBelowAvg, totalPotentialImpact, avgPerformanceGap, comparableCount, impactCount } = scorecardStats(kpiData, isLowerBetter);

  // Show error state if there's an error
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Assessment Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col gap-3">
            <Link to="/kpi-scorecard">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                Start New Assessment
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full">
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 text-emerald-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm sm:text-base">Loading your analysis...</p>
        </div>
      </div>
    );
  }

  // Sample Preview Component - shown when user doesn't have full access OR data is sample preview
  // This MUST be shown when:
  // 1. hasFullAccess is false (user hasn't verified code), OR
  // 2. The backend returned is_sample_preview=true (limited data)
  const shouldShowSamplePreview = analysis && (!hasFullAccess || analysis.is_sample_preview === true);
  
  if (shouldShowSamplePreview) {
    const sampleKpis = (analysis.kpis || []).slice(0, 3); // Only show 3 sample KPIs
    const totalKpisCount = analysis.total_kpis_count || (analysis.kpis || []).length;
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">S</span>
              </div>
              <span className="text-xl font-bold text-gray-900">SAMANVAI</span>
            </Link>
            <Button variant="outline" onClick={handleLogout} className="gap-2">
              <LogOut className="w-4 h-4" />
              Exit
            </Button>
          </div>
        </header>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Sample Report Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <FileBarChart2 className="w-4 h-4" />
              Sample Value Scorecard Preview
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
              {analysis.company_name || companyName}
            </h1>
            <p className="text-gray-600">Industry: {analysis.sector}</p>
          </div>

          {/* Sample Executive Summary */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-emerald-600" />
              Executive Summary (Preview)
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {(analysis.executive_summary || '').substring(0, 300)}...
            </p>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <p className="text-sm text-gray-500 italic">Full executive summary available with access code</p>
            </div>
          </div>

          {/* Underperforming KPIs - Highlighted in Red */}
          {(() => {
            // Helper to check if KPI is underperforming
            const isUnderperforming = (kpi) => {
              if (kpi.status === 'below_average') return true;
              const yourVal = kpi.yourValue ?? kpi.company_value;
              const industryVal = kpi.industryAvg ?? kpi.industry_average;
              if (yourVal != null && industryVal != null) {
                return parseFloat(yourVal) < parseFloat(industryVal);
              }
              return false;
            };
            
            // Format value with unit
            const formatVal = (val, unit) => {
              if (val == null || val === 'N/A') return 'N/A';
              if (typeof val === 'number') {
                if (unit === '%') return `${val.toFixed(1)}%`;
                if (val >= 1000000) return `${curSym}${(val/1000000).toFixed(1)}M`;
                if (val >= 1000) return `${curSym}${(val/1000).toFixed(0)}K`;
                return val.toFixed(1);
              }
              return String(val);
            };
            
            const underperformingKpis = (analysis.kpis || []).filter(isUnderperforming).slice(0, 2);
            if (underperformingKpis.length === 0) return null;
            
            return (
              <div className="bg-white rounded-2xl shadow-sm border-2 border-red-200 p-6 mb-6">
                <h2 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  ⚠️ Underperforming Metrics Requiring Attention
                </h2>
                <div className="space-y-4">
                  {underperformingKpis.map((kpi, idx) => {
                    const unit = kpi.unit || '';
                    const yourValue = kpi.yourValue ?? kpi.company_value;
                    const industryAvg = kpi.industryAvg ?? kpi.industry_average;
                    const topPerformer = kpi.topPerformer ?? kpi.top_performer;
                    const impact = kpi.impactPer1Percent ?? kpi.improvement_value;
                    
                    return (
                      <div key={idx} className="p-4 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-bold text-red-800">{kpi.name}</span>
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-600 text-white">
                            BELOW AVERAGE
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                          <div>
                            <span className="text-red-600">Your Value:</span>
                            <span className="ml-2 font-bold text-red-800">{formatVal(yourValue, unit)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Industry Avg:</span>
                            <span className="ml-2 font-medium text-gray-800">{formatVal(industryAvg, unit)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Top Performer:</span>
                            <span className="ml-2 font-medium text-emerald-600">{formatVal(topPerformer, unit)}</span>
                          </div>
                        </div>
                        <div className="p-2 bg-red-100 rounded border border-red-300">
                          <p className="text-xs text-red-700">
                            <strong>Potential Impact:</strong> {formatVal(impact)} per 1% improvement opportunity identified
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-300">
                  <p className="text-sm text-amber-800 font-medium">
                    📋 <strong>Detailed recommendations and action plans to address these underperforming metrics are available in the full report.</strong>
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Enter your secret code below to unlock strategic insights, improvement roadmaps, and implementation guidance.
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Sample KPIs */}
          {(() => {
            // Helper functions defined in scope
            const isUnderperforming = (kpi) => {
              if (kpi.status === 'below_average') return true;
              const yourVal = kpi.yourValue ?? kpi.company_value;
              const industryVal = kpi.industryAvg ?? kpi.industry_average;
              if (yourVal != null && industryVal != null) {
                return parseFloat(yourVal) < parseFloat(industryVal);
              }
              return false;
            };
            
            const formatVal = (val, unit) => {
              if (val == null || val === 'N/A') return 'N/A';
              if (typeof val === 'number') {
                if (unit === '%') return `${val.toFixed(1)}%`;
                if (val >= 1000000) return `${curSym}${(val/1000000).toFixed(1)}M`;
                if (val >= 1000) return `${curSym}${(val/1000).toFixed(0)}K`;
                return val.toFixed(1);
              }
              return String(val);
            };
            
            return (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                  Sample KPI Analysis (3 of {totalKpisCount})
                </h2>
                <div className="space-y-4">
                  {sampleKpis.map((kpi, idx) => {
                    const underperforming = isUnderperforming(kpi);
                    const unit = kpi.unit || '';
                    const yourValue = kpi.yourValue ?? kpi.company_value;
                    const industryAvg = kpi.industryAvg ?? kpi.industry_average;
                    const impact = kpi.impactPer1Percent ?? kpi.improvement_value;
                    
                    return (
                      <div key={idx} className={`p-4 rounded-lg ${
                        underperforming 
                          ? 'bg-red-50 border border-red-200' 
                          : 'bg-gray-50'
                      }`}>
                        <div className="flex justify-between items-start mb-2">
                          <span className={`font-medium ${underperforming ? 'text-red-800' : 'text-gray-900'}`}>
                            {kpi.name}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            underperforming 
                              ? 'bg-red-600 text-white' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {underperforming ? '⚠️ Below Avg' : '✓ On Track'}
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Company:</span>
                            <span className={`ml-2 font-medium ${underperforming ? 'text-red-700' : ''}`}>
                              {formatVal(yourValue, unit)}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Industry:</span>
                            <span className="ml-2 font-medium">{formatVal(industryAvg, unit)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Impact:</span>
                            <span className="ml-2 font-medium text-emerald-600">{formatVal(impact)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-sm text-gray-500 italic">
                    {totalKpisCount - 3 > 0 ? `${totalKpisCount - 3} more KPIs available with full access` : 'Enter your secret code to see the full report'}
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Blurred Preview Section */}
          <div className="relative mb-8">
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/80 to-white z-10 flex items-end justify-center pb-8">
              <div className="text-center">
                <p className="text-gray-500 mb-2">Additional sections include:</p>
                <div className="flex flex-wrap justify-center gap-2 mb-4">
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">Industry Benchmarks</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">5-Year Roadmap</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">Impact Analysis</span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-sm">EOAI Framework</span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 filter blur-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-4">5-Year Strategic Roadmap</h2>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-16 bg-gray-100 rounded-lg" />
                ))}
              </div>
            </div>
          </div>

          {/* Access Code Section */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-8 text-white text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Unlock Full Value Scorecard</h2>
            <p className="text-emerald-100 mb-6 max-w-md mx-auto">
              Enter your 6-digit secret code to access the complete dashboard with detailed KPI analysis, 
              benchmarks, roadmap, and downloadable reports.
            </p>
            
            <div className="max-w-sm mx-auto">
              <div className="flex gap-2 mb-3">
                <Input
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={secretCode}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setSecretCode(val);
                    setCodeError('');
                  }}
                  className="text-center text-lg tracking-widest font-mono bg-white text-gray-900"
                  maxLength={6}
                  data-testid="secret-code-input"
                />
                <Button 
                  onClick={handleVerifyCode}
                  disabled={verifyingCode || secretCode.length !== 6}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 px-6"
                  data-testid="verify-code-btn"
                >
                  {verifyingCode ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Unlock'}
                </Button>
              </div>
              
              {codeError && (
                <p className="text-red-200 text-sm mb-3">{codeError}</p>
              )}
              
              <p className="text-emerald-200 text-sm">
                For full report, please enter secret code.
              </p>
              <p className="text-emerald-100 text-sm mt-2">
                Contact <strong>Vijay Karna</strong> at{' '}
                <a href="mailto:karna.vijay@gmail.com" className="underline hover:text-white">
                  karna.vijay@gmail.com
                </a>
              </p>
            </div>
          </div>

          {/* Back to Home */}
          <div className="text-center mt-8">
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <ArrowRight className="w-4 h-4 rotate-180" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100" data-testid="dashboard-page">
      {/* Free Trial Modal */}
      <FreeTrialModal 
        isOpen={showTrialModal} 
        onClose={() => {
          setShowTrialModal(false);
          const storedUserId = localStorage.getItem('samanvai_user_id');
          if (storedUserId) {
            setUserId(storedUserId);
            setUserName(localStorage.getItem('samanvai_user_name') || 'User');
            setCompanyName(localStorage.getItem('samanvai_company') || 'Your Company');
            fetchAnalysis(storedUserId);
          }
        }} 
      />

      {/* Top Nav - Mobile Optimized */}
      <header className="fixed top-0 left-0 right-0 h-14 sm:h-16 bg-white border-b border-gray-200 z-50 flex items-center justify-between px-3 sm:px-4">
        <div className="flex items-center gap-2 sm:gap-4">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg lg:hidden"
            data-testid="toggle-sidebar"
          >
            <Menu className="w-5 h-5" />
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm sm:text-lg">S</span>
            </div>
            <span className="text-lg sm:text-xl font-bold text-gray-900 hidden sm:block">SAMANVAI</span>
          </Link>
        </div>

        <div className="flex items-center gap-2 sm:gap-4">
          <button className="p-2 hover:bg-gray-100 rounded-lg relative hidden sm:block">
            <Bell className="w-5 h-5 text-gray-600" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-lg" onClick={handleLogout} title="Logout" data-testid="logout-btn">
            <LogOut className="w-5 h-5 text-gray-600" />
          </button>
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-emerald-600 rounded-full flex items-center justify-center">
            <span className="text-white font-medium text-xs sm:text-sm">
              {userName.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex pt-14 sm:pt-16">
        {/* Sidebar - Mobile Slide-out */}
        <aside className={`fixed left-0 top-14 sm:top-16 bottom-0 w-64 bg-white border-r border-gray-200 transition-transform z-40 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`}>
          <div className="flex items-center justify-between p-4 lg:hidden border-b border-gray-200">
            <span className="font-semibold text-gray-900">Menu</span>
            <button onClick={() => setSidebarOpen(false)} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <nav className="p-4 space-y-2">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 px-3">
              Dashboard
            </div>
            <button 
              onClick={() => { setActiveTab('overview'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'overview' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-overview"
            >
              <BarChart3 className="w-5 h-5" />
              Overview
            </button>
            <button 
              onClick={() => { setActiveTab('kpis'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'kpis' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-kpis"
            >
              <Target className="w-5 h-5" />
              KPI Analysis
            </button>
            <button 
              onClick={() => { setActiveTab('peers'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'peers' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-peers"
            >
              <Building2 className="w-5 h-5" />
              Peer Comparison
              {peers.length > 0 && (
                <span className="ml-auto bg-emerald-100 text-emerald-600 text-xs px-2 py-0.5 rounded-full">
                  {peers.length}
                </span>
              )}
            </button>
            <button 
              onClick={() => { setActiveTab('roadmap'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'roadmap' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-roadmap"
            >
              <Zap className="w-5 h-5" />
              5-Year Roadmap
            </button>
            {COMPANY_DASHBOARD_TOOLS_ENABLED && <>
            <button 
              onClick={() => { setActiveTab('proposal'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'proposal' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-proposal"
            >
              <FileText className="w-5 h-5" />
              High-Level Proposal
              <span className="ml-auto bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                AI
              </span>
            </button>
            <button 
              onClick={() => { setActiveTab('agentic'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'agentic' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-agentic"
            >
              <Bot className="w-5 h-5" />
              AI Consultant
              <span className="ml-auto bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium animate-pulse">
                NEW
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('agi'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'agi' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-agi"
            >
              <Brain className="w-5 h-5" />
              AGI Insights
              <span className="ml-auto bg-gradient-to-r from-emerald-500 to-violet-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium animate-pulse">
                AGI
              </span>
            </button>
            <button
              onClick={() => { setActiveTab('audit'); setSidebarOpen(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-sm sm:text-base ${
                activeTab === 'audit' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid="tab-audit"
            >
              <FileText className="w-5 h-5" />
              Audit Trail
            </button>
            </>}
            
            <div className="border-t border-gray-200 my-4" />
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-3">
              Reports
            </div>
            <button 
              onClick={() => navigate('/report')}
              className="flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 transition-colors text-sm sm:text-base"
              data-testid="btn-report"
            >
              <FileBarChart2 className="w-5 h-5" />
              Value Scorecard
              <span className="ml-auto bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
                NEW
              </span>
            </button>
            <button
              onClick={() => { setSidebarOpen(false); navigate(`/report?id=${encodeURIComponent(userId)}#client-proposal`); }}
              disabled={!userId}
              className="flex items-center gap-3 px-3 py-2.5 sm:py-2 rounded-lg w-full text-left text-emerald-700 hover:bg-emerald-50 transition-colors text-sm sm:text-base disabled:opacity-50"
              data-testid="btn-client-proposal"
            >
              <FileText className="w-5 h-5 shrink-0" />
              <span>Create Client Proposal</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className={`flex-1 p-3 sm:p-6 transition-all lg:ml-64`}>
          {/* Header */}
          <div className="flex flex-col gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Executive Dashboard</h1>
              <div className="flex items-center gap-2 flex-wrap mt-1">
                <p className="text-sm sm:text-base text-gray-600">
                  <span className="font-semibold text-emerald-600">{companyName}</span>
                  {analysis?.sector && <span className="text-gray-400"> • {analysis.sector}</span>}
                </p>
                {analysis?.data_source && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    analysis.data_source === 'uploaded_reports' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {analysis.data_source === 'uploaded_reports' ? (
                      <><FileText className="w-3 h-3" /> Reports</>
                    ) : (
                      <><Globe className="w-3 h-3" /> Public Data</>
                    )}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="text-xs sm:text-sm h-9 sm:h-10">
                <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
              <Button 
                size="sm" 
                onClick={() => navigate('/report')}
                className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-xs sm:text-sm h-9 sm:h-10"
                data-testid="btn-generate-report"
              >
                <FileBarChart2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Value</span> Scorecard
              </Button>
            </div>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Data Governance banner */}
              {analysis?.data_governance && (analysis.data_governance.reporting_period_used || analysis.data_governance.data_current_as_of) && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-1" data-testid="data-governance-banner">
                  {analysis.data_governance.reporting_period_used && (
                    <span className="text-xs text-slate-600"><span className="font-semibold text-slate-800">Reporting period:</span> {analysis.data_governance.reporting_period_used}</span>
                  )}
                  {analysis.data_governance.period_requested && (
                    <span className="text-xs text-slate-600" data-testid="governance-period-requested"><span className="font-semibold text-slate-800">Requested:</span> {analysis.data_governance.period_requested}</span>
                  )}
                  {analysis.data_governance.report_generated_on && (
                    <span className="text-xs text-slate-600" data-testid="governance-generated-on"><span className="font-semibold text-slate-800">Generated on:</span> {analysis.data_governance.report_generated_on}</span>
                  )}
                  {analysis.data_governance.data_current_as_of && (
                    <span className="text-xs text-slate-600"><span className="font-semibold text-slate-800">Data current as of:</span> {analysis.data_governance.data_current_as_of}</span>
                  )}
                  {(analysis.data_governance.currency || analysis.currency) && (
                    <span className="text-xs text-slate-600" data-testid="governance-currency" title={analysis.data_governance.fx_note || ''}><span className="font-semibold text-slate-800">Currency:</span> {analysis.data_governance.currency || analysis.currency}{analysis.data_governance.fx_note ? ` · ${String(analysis.data_governance.fx_note).length > 90 ? String(analysis.data_governance.fx_note).slice(0, 90) + '…' : analysis.data_governance.fx_note}` : ''}</span>
                  )}
                  {analysis.data_governance.stale_period && (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5" data-testid="governance-stale-warning">
                      ⚠ Older reporting period — newer filings may exist; verify before circulation
                    </span>
                  )}
                  <span className="text-xs text-slate-500 italic">AI-generated opportunity hypothesis — validate against internal actuals before decisions.</span>
                </div>
              )}
              {/* Summary Cards - Mobile 2-col grid */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <span className="text-gray-500 text-xs sm:text-sm">Above Avg</span>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                    </div>
                  </div>
                  <div data-testid="summary-above-average" className="text-2xl sm:text-3xl font-bold text-gray-900">{kpisAboveAvg ?? '—'}</div>
                  <div className="text-xs sm:text-sm text-emerald-600 flex items-center gap-1 mt-1">
                    <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4" />
                    of {comparableCount} comparable
                  </div>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <span className="text-gray-500 text-xs sm:text-sm">Below Avg</span>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                      <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                    </div>
                  </div>
                  <div data-testid="summary-below-average" className="text-2xl sm:text-3xl font-bold text-gray-900">{kpisBelowAvg ?? '—'}</div>
                  <div className="text-xs sm:text-sm text-amber-600 flex items-center gap-1 mt-1">
                    <AlertTriangle className="w-3 h-3 sm:w-4 sm:h-4" />
                    Attention
                  </div>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <span className="text-gray-500 text-xs sm:text-sm">Gap to Top</span>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Target className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                    </div>
                  </div>
                  <div data-testid="summary-gap-to-top" className="text-2xl sm:text-3xl font-bold text-gray-900">{avgPerformanceGap === null ? '—' : `${avgPerformanceGap}%`}</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">Avg gap</div>
                </div>

                <div className="bg-white rounded-xl p-3 sm:p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-2 sm:mb-4">
                    <span className="text-gray-500 text-xs sm:text-sm">1% Impact</span>
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <Coins className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                    </div>
                  </div>
                  <div data-testid="summary-financial-impact" className="text-xl sm:text-2xl font-bold text-gray-900">{impactText(totalPotentialImpact, curSym, 0)}</div>
                  <div data-testid="summary-impact-coverage" className="text-xs sm:text-sm text-gray-500 mt-1">{impactCount} of {kpiData.length} quantified</div>
                </div>
              </div>

              {/* Executive Summary - Collapsible on mobile */}
              {analysis?.executive_summary && (
                <CollapsibleCard title="Executive Summary" icon={FileText} defaultOpen={true}>
                  <div className="p-4 sm:p-6">
                    <p className="text-sm sm:text-base text-gray-600 whitespace-pre-line">{analysis.executive_summary}</p>
                  </div>
                </CollapsibleCard>
              )}

              {/* KPI Table - Collapsible with horizontal scroll */}
              <CollapsibleCard 
                title={`Top ${kpiData.length} KPIs`} 
                icon={Target} 
                defaultOpen={true}
                badge={kpisBelowAvg === null ? 'Benchmarks unavailable' : `${kpisBelowAvg} need attention`}
              >
                <div className="p-3 sm:p-4 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                      <span className="text-red-600">Underperforming</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                      <span className="text-emerald-600">Above Average</span>
                    </span>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">KPI</th>
                        <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Your Value</th>
                        <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Industry</th>
                        <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-gray-500 uppercase">Top</th>
                        <th className="px-3 sm:px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase">1% Impact</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {kpiData.map((kpi, index) => {
                        const lowerIsBetter = isLowerBetter(kpi.name);
                        const comparison = metricComparison(kpi, lowerIsBetter);
                        const isBelowAvg = comparison === -1;
                        
                        return (
                          <tr data-testid={`overview-kpi-row-${index}`} key={index} className={`hover:bg-gray-50 ${isBelowAvg ? 'bg-red-50/50' : ''}`}>
                            <td className="px-3 sm:px-6 py-3 sm:py-4">
                              <div className="flex items-center gap-2">
                                {isBelowAvg && (
                                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></span>
                                )}
                                <span className="font-medium text-gray-900 text-sm">{kpi.name}</span>
                                {(kpi.basis || kpi.confidence) && (
                                  <span
                                    title={[kpi.source, kpi.boundary, kpi.period].filter(Boolean).join(' · ')}
                                    className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 text-[10px] uppercase tracking-wide flex-shrink-0"
                                    data-testid={`kpi-basis-chip-${index}`}
                                  >
                                    {[kpi.basis, kpi.confidence && `conf ${kpi.confidence}`].filter(Boolean).join(' · ')}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-center">
                              <span data-testid={`overview-kpi-value-${index}`} className={`font-semibold text-sm ${isBelowAvg ? 'text-red-600' : comparison === 1 ? 'text-emerald-600' : 'text-gray-700'}`}>
                                {metricText(kpi.yourValue, formatUnit(kpi.unit))}
                              </span>
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-gray-500 text-sm">
                              <span data-testid={`overview-kpi-industry-${index}`}>{metricText(kpi.industryAvg, formatUnit(kpi.unit))}</span>
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-yellow-600 text-sm">
                              <span data-testid={`overview-kpi-top-${index}`}>{metricText(kpi.topPerformer, formatUnit(kpi.unit))}</span>
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-right font-medium text-gray-900 text-sm">
                              <span data-testid={`overview-kpi-impact-${index}`}>{impactText(kpi.impactPer1Percent, curSym)}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CollapsibleCard>
            </div>
          )}

          {/* KPIs Tab - Areas of Improvement */}
          {activeTab === 'kpis' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-2">Areas of Improvement</h2>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">Tap each area to expand details</p>
                <div className="space-y-3 sm:space-y-4">
                  {areasOfImprovement.map((item, index) => (
                    <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                      {/* Clickable Header */}
                      <button
                        onClick={() => toggleAreaExpansion(index)}
                        className={`w-full p-3 sm:p-4 flex items-center justify-between ${
                          item.priority === 'High' ? 'bg-gradient-to-r from-red-500 to-red-600' :
                          item.priority === 'Medium' ? 'bg-gradient-to-r from-amber-500 to-amber-600' :
                          'bg-gradient-to-r from-gray-500 to-gray-600'
                        } text-white`}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                          <Target className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                          <div className="text-left min-w-0">
                            <h3 className="font-bold text-sm sm:text-lg truncate">{item.area}</h3>
                            <p className="text-xs sm:text-sm opacity-90 truncate">{item.potentialImpact}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="hidden sm:inline px-2 sm:px-3 py-1 bg-white/20 rounded-full text-xs sm:text-sm font-medium">
                            {item.priority}
                          </span>
                          {expandedAreas[index] ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </div>
                      </button>

                      {/* Expandable Content */}
                      {expandedAreas[index] && (
                        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                          {/* Solution */}
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <Zap className="w-4 h-4 text-emerald-600" />
                              Solution
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 bg-emerald-50 rounded-lg p-3 sm:p-4">
                              {item.solution || "Strategic initiatives to address this improvement area."}
                            </p>
                          </div>

                          {/* Cost & Timeline - Mobile stacked */}
                          <div className="grid grid-cols-2 gap-3 sm:gap-4">
                            <div className="bg-blue-50 rounded-lg p-3 sm:p-4 text-center">
                              <p className="text-xs text-blue-600 font-medium">Est. Cost</p>
                              <p className="text-sm sm:text-lg font-bold text-blue-800">{item.estimated_cost || "TBD"}</p>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-3 sm:p-4 text-center">
                              <p className="text-xs text-purple-600 font-medium">Timeline</p>
                              <p className="text-sm sm:text-lg font-bold text-purple-800">{item.timeline || "12-18 mo"}</p>
                            </div>
                          </div>

                          {/* Implementation Plan */}
                          {item.implementation_plan && item.implementation_plan.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                                <Wrench className="w-4 h-4 text-amber-600" />
                                Implementation
                              </h4>
                              <ol className="space-y-2">
                                {item.implementation_plan.map((step, stepIndex) => (
                                  <li key={stepIndex} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                                    <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 font-bold text-xs">
                                      {stepIndex + 1}
                                    </span>
                                    <span className="pt-0.5">{step}</span>
                                  </li>
                                ))}
                              </ol>
                            </div>
                          )}

                          {/* Resources */}
                          {item.resources_required && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                                <Users className="w-4 h-4 text-indigo-600" />
                                Resources
                              </h4>
                              <div className="bg-indigo-50 rounded-lg p-3 sm:p-4">
                                <p className="text-indigo-800 text-xs sm:text-sm">{item.resources_required}</p>
                              </div>
                            </div>
                          )}

                          {/* Case Study */}
                          {item.case_study && (
                            <div className="pt-4 border-t border-gray-200">
                              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm sm:text-base">
                                <Building2 className="w-4 h-4 text-teal-600" />
                                Case Study
                              </h4>
                              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl p-4 border border-teal-100">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center text-white font-bold">
                                    {item.case_study.company?.charAt(0) || "C"}
                                  </div>
                                  <div>
                                    <p className="font-bold text-gray-900 text-sm">{item.case_study.company || "Industry Leader"}</p>
                                    <p className="text-xs text-teal-600">{item.case_study.industry || "Similar Industry"}</p>
                                  </div>
                                </div>
                                <div className="space-y-2 text-xs sm:text-sm">
                                  <div>
                                    <p className="font-semibold text-red-600">Challenge</p>
                                    <p className="text-gray-600">{item.case_study.challenge}</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-blue-600">Solution</p>
                                    <p className="text-gray-600">{item.case_study.solution}</p>
                                  </div>
                                  <div>
                                    <p className="font-semibold text-emerald-600">Results</p>
                                    <p className="text-gray-600 font-medium">{item.case_study.results}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Peer Comparison Tab */}
          {activeTab === 'peers' && peers.length === 0 && (
            <div className="space-y-4 sm:space-y-6" data-testid="peer-comparison-empty">
              <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">
                      No credibly-comparable peers were auto-selected
                    </h2>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4">
                      Our credibility-first peer engine will not pad this report with unrelated companies.
                      For <span className="font-medium text-gray-900">{companyName || 'this company'}</span> we could not confidently identify
                      3 real, currently-operating peers that share the same sub-industry, business model, customer segment and geography.
                    </p>
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700 space-y-2">
                      <div className="font-semibold text-gray-900">You can do one of the following:</div>
                      <ul className="list-disc pl-5 space-y-1.5">
                        <li>Re-run the assessment and manually enter 1–3 peer companies in Step 3 of the signup flow.</li>
                        <li>Double-check that the sector and company URL you provided are accurate — misleading company names + broad sectors often make it hard for the engine to find credible peers.</li>
                        <li>Contact us if you would like a human analyst to curate peers for a niche or emerging sub-industry.</li>
                      </ul>
                    </div>
                    <div className="mt-4 text-xs text-gray-500 italic">
                      Note: an empty peer list here is intentional — it protects the credibility of your report versus fabricated or unrelated benchmarks.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'peers' && peers.length > 0 && (
            <div className="space-y-4 sm:space-y-6">
              <CollapsibleCard 
                title="Peer Comparison" 
                icon={Building2} 
                defaultOpen={true}
                badge={`vs ${peers.length} peers`}
              >
                <div className="p-3 sm:p-4 bg-gray-50 border-b">
                  <p className="text-xs sm:text-sm text-gray-600">
                    Comparing with: <span className="font-medium">{peers.join(', ')}</span>
                  </p>
                  {peerComparison.some((pc) => pc.comparability) && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="peer-comparability-legend">
                      Peer values are AI estimates unless marked reported. Tags: comparable = like-for-like basis · adjusted = normalised for scale/mix · directional = indicative only.
                    </p>
                  )}
                  {hiddenPeerRows > 0 && (
                    <p className="text-[11px] text-gray-500 mt-1" data-testid="peer-hidden-rows-note">
                      {hiddenPeerRows} KPI{hiddenPeerRows > 1 ? 's' : ''} hidden — no reliable peer data available for {hiddenPeerRows > 1 ? 'them' : 'it'}.
                    </p>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">KPI</th>
                        <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-emerald-600 uppercase bg-emerald-50">You</th>
                        {peers[0] && <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-blue-600 uppercase">{peers[0].slice(0, 10)}</th>}
                        {peers[1] && <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-purple-600 uppercase">{peers[1].slice(0, 10)}</th>}
                        {peers[2] && <th className="px-3 sm:px-6 py-3 text-center text-xs font-semibold text-orange-600 uppercase">{peers[2].slice(0, 10)}</th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {peerComparison.map((pc, index) => {
                        const lowerIsBetter = isLowerBetter(pc.name);
                        const isBelowIndustryAvg = metricComparison(pc, lowerIsBetter) === -1;
                        
                        return (
                          <tr key={index} className={`hover:bg-gray-50 ${isBelowIndustryAvg ? 'bg-red-50/30' : ''}`}>
                            <td className="px-3 sm:px-6 py-3 sm:py-4">
                              <div className="flex items-center gap-2">
                                {isBelowIndustryAvg && (
                                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0"></span>
                                )}
                                <span className="font-medium text-gray-900 text-xs sm:text-sm">{pc.name}</span>
                                {pc.comparability && (
                                  <span
                                    title={pc.comparability_note || ''}
                                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium flex-shrink-0 ${
                                      pc.comparability === 'comparable' ? 'bg-emerald-50 text-emerald-600' :
                                      pc.comparability === 'adjusted' ? 'bg-blue-50 text-blue-600' :
                                      'bg-amber-50 text-amber-600'
                                    }`}
                                    data-testid={`peer-comparability-badge-${index}`}
                                  >
                                    {pc.comparability}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-3 sm:px-6 py-3 sm:py-4 text-center bg-emerald-50">
                              <span className={`font-semibold text-xs sm:text-sm ${isBelowIndustryAvg ? 'text-red-600' : 'text-emerald-600'}`}>
                                <span data-testid={`peer-company-value-${index}`}>{metricText(pc.yourValue, formatUnit(pc.unit))}</span>
                              </span>
                            </td>
                            {peers[0] && (
                              <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600">
                                {pc.peer1Value != null ? `${pc.peer1Value}${formatUnit(pc.unit)}` : '-'}
                              </td>
                            )}
                            {peers[1] && (
                              <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600">
                                {pc.peer2Value != null ? `${pc.peer2Value}${formatUnit(pc.unit)}` : '-'}
                              </td>
                            )}
                            {peers[2] && (
                              <td className="px-3 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-sm text-gray-600">
                                {pc.peer3Value != null ? `${pc.peer3Value}${formatUnit(pc.unit)}` : '-'}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CollapsibleCard>

              {/* Visual Cards - Mobile 1 col */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {peerComparison.slice(0, 6).map((pc, index) => {
                  const allValues = [
                    { name: 'You', value: pc.yourValue, color: 'emerald' },
                    ...(peers[0] && pc.peer1Value != null ? [{ name: peers[0].slice(0, 12), value: pc.peer1Value, color: 'blue' }] : []),
                    ...(peers[1] && pc.peer2Value != null ? [{ name: peers[1].slice(0, 12), value: pc.peer2Value, color: 'purple' }] : []),
                    ...(peers[2] && pc.peer3Value != null ? [{ name: peers[2].slice(0, 12), value: pc.peer3Value, color: 'orange' }] : []),
                  ].filter(item => hasMetric(item.value)).sort((a, b) => b.value - a.value);
                  
                  const maxValue = Math.max(...allValues.map(v => v.value));
                  
                  return (
                    <div key={index} className="bg-white rounded-xl p-4 sm:p-6 shadow-sm">
                      <h4 className="font-semibold text-gray-900 mb-4 text-sm sm:text-base">{pc.name}</h4>
                      <div className="space-y-3">
                        {allValues.map((item, i) => (
                          <div key={i}>
                            <div className="flex justify-between text-xs sm:text-sm mb-1">
                              <span className={`font-medium ${item.name === 'You' ? 'text-emerald-600' : 'text-gray-600'}`}>
                                {item.name}
                              </span>
                              <span className="font-semibold">{item.value}{formatUnit(pc.unit)}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full transition-all duration-500 ${
                                  item.color === 'emerald' ? 'bg-emerald-500' :
                                  item.color === 'blue' ? 'bg-blue-500' :
                                  item.color === 'purple' ? 'bg-purple-500' :
                                  'bg-orange-500'
                                }`}
                                style={{ width: `${maxValue ? Math.max(0, (item.value / maxValue) * 100) : 0}%` }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Roadmap Tab */}
          {activeTab === 'roadmap' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-4 sm:mb-6">5-Year Strategic Roadmap</h2>
                <div className="space-y-4 sm:space-y-6">
                  {fiveYearRoadmap.map((year, index) => (
                    <CollapsibleCard 
                      key={index}
                      title={`Year ${index + 1}: ${year.focus}`}
                      defaultOpen={index === 0}
                      badge={year.estimated_investment}
                    >
                      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
                        {/* Goals */}
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                            <Target className="w-4 h-4 text-emerald-600" />
                            Goals
                          </h4>
                          <ul className="space-y-2">
                            {year.goals?.map((goal, gIndex) => (
                              <li key={gIndex} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                                <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                                {goal}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* How to Achieve */}
                        {year.how_to_achieve && year.how_to_achieve.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <Wrench className="w-4 h-4 text-blue-600" />
                              How to Achieve
                            </h4>
                            <ul className="space-y-2">
                              {year.how_to_achieve.map((step, sIndex) => (
                                <li key={sIndex} className="flex items-start gap-2 text-xs sm:text-sm text-gray-600">
                                  <ChevronRight className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                  {step}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Technology */}
                        {year.technology_required && year.technology_required.length > 0 && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <Cpu className="w-4 h-4 text-purple-600" />
                              Technology
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {year.technology_required.map((tech, tIndex) => (
                                <span 
                                  key={tIndex} 
                                  className="bg-purple-50 text-purple-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium"
                                >
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Manpower */}
                        {year.manpower_required && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <UserCheck className="w-4 h-4 text-amber-600" />
                              Manpower
                            </h4>
                            <div className="bg-amber-50 rounded-lg p-3">
                              <p className="text-amber-800 text-xs sm:text-sm">{year.manpower_required}</p>
                            </div>
                          </div>
                        )}

                        {/* Decision Gate */}
                        {year.decision_gate && (
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                              Decision Gate
                            </h4>
                            <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
                              <p className="text-emerald-800 text-xs sm:text-sm" data-testid={`roadmap-decision-gate-${index}`}>
                                <span className="font-semibold">Go/no-go before next year's spend: </span>{year.decision_gate}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CollapsibleCard>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Audit Trail Tab */}
          {COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'audit' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 mb-1">Audit Trail</h2>
                <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">Complete change log: when this scorecard was generated, verified and exported — and with what settings.</p>
                {auditLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm py-8 justify-center"><RefreshCw className="w-4 h-4 animate-spin" /> Loading audit trail...</div>
                ) : auditLog.length === 0 ? (
                  <p className="text-sm text-gray-500 py-8 text-center" data-testid="audit-trail-empty">No audit entries yet.</p>
                ) : (
                  <div className="space-y-3" data-testid="audit-trail-list">
                    {auditLog.map((entry, idx) => {
                      const meta = {
                        analysis_generated: { label: 'Analysis Generated', color: 'bg-emerald-100 text-emerald-700' },
                        report_generated: { label: 'Report Generated', color: 'bg-blue-100 text-blue-700' },
                        pdf_downloaded: { label: 'PDF Downloaded', color: 'bg-purple-100 text-purple-700' },
                        code_verified: { label: 'Access Verified', color: 'bg-amber-100 text-amber-700' },
                      }[entry.event] || { label: entry.event, color: 'bg-gray-100 text-gray-700' };
                      return (
                        <div key={entry.id || idx} className="border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4" data-testid={`audit-entry-${idx}`}>
                          <div className="flex items-center gap-3 sm:min-w-[180px]">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${meta.color}`}>{meta.label}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-400 mb-1.5">{entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'Unknown time'}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {Object.entries(entry.details || {}).filter(([, v]) => v !== '' && v !== null && (Array.isArray(v) ? v.length > 0 : true)).map(([k, v]) => (
                                <span key={k} className="bg-gray-50 border border-gray-200 rounded px-2 py-0.5 text-[11px] text-gray-600">
                                  <span className="font-medium text-gray-800">{k.replace(/_/g, ' ')}:</span> {Array.isArray(v) ? v.join(', ') : String(v)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Proposal Tab */}
          {COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'proposal' && (
            <div className="space-y-4 sm:space-y-6 break-words" data-testid="high-level-proposal-tab">
              {proposalLoading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 sm:p-16 text-center" role="status" data-testid="proposal-loading">
                  <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating High-Level Proposal</h3>
                  <p className="text-gray-500 text-sm">Our AI is analyzing your data and creating a comprehensive proposal...</p>
                  <p className="text-gray-400 text-xs mt-2" data-testid="proposal-processing-message">Your assessment is saved. Proposal generation is in progress.</p>
                </div>
              ) : proposalError ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center" role="alert" data-testid="proposal-error">
                  <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Generating Proposal</h3>
                  <p className="text-gray-500 text-sm mb-4" data-testid="proposal-error-message">{proposalError}</p>
                  <Button onClick={() => fetchOrGenerateProposal(true)} data-testid="proposal-retry-button" className="bg-emerald-600 hover:bg-emerald-700">
                    <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                  </Button>
                </div>
              ) : proposal ? (
                <>
                  {/* Executive Summary */}
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl shadow-sm p-6 sm:p-8 text-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-4">High-Level Proposal</h2>
                    <h3 className="text-lg font-semibold mb-2 text-purple-200">Executive Summary</h3>
                    <p className="text-sm sm:text-base text-purple-100 leading-relaxed whitespace-pre-line" data-testid="proposal-executive-summary">
                      {proposal.executive_summary}
                    </p>
                  </div>

                  {/* Areas of Concern */}
                  <CollapsibleCard title="Areas of Concern from Value Scorecard" icon={AlertTriangle} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(proposal.areas_of_concern || []).map((concern, idx) => (
                          <div key={idx} className="bg-red-50 border border-red-100 rounded-lg p-4">
                            <h4 className="font-semibold text-red-800 text-sm mb-2">
                              {concern.kpi_name || concern.area}
                            </h4>
                            {concern.current_value && (
                              <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>Current: <strong className="text-red-600">{concern.current_value}</strong></span>
                                <span>Industry Avg: <strong>{concern.industry_avg}</strong></span>
                              </div>
                            )}
                            {concern.gap && (
                              <p className="text-xs text-red-600">Gap: {concern.gap}</p>
                            )}
                            {concern.priority && (
                              <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded ${concern.priority === 'High' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                                {concern.priority} Priority
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Proposed Technical Solution */}
                  <CollapsibleCard title="Proposed Technical Solution" icon={Cpu} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{proposal.proposed_technical_solution?.overview}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800 text-sm mb-2">Key Components</h4>
                          <ul className="space-y-1">
                            {(proposal.proposed_technical_solution?.key_components || []).map((comp, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                {comp}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-800 text-sm mb-2">Benefits</h4>
                          <ul className="space-y-1">
                            {(proposal.proposed_technical_solution?.benefits || []).map((benefit, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                <TrendingUp className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* System Architecture */}
                  <CollapsibleCard title="System Architecture and Design" icon={Globe} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{proposal.system_architecture?.description}</p>
                      {proposal.system_architecture?.components?.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {proposal.system_architecture.components.map((comp, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <h5 className="font-semibold text-gray-900 text-sm">{comp.name}</h5>
                              <p className="text-xs text-gray-500 mt-1">{comp.description}</p>
                              {comp.technology && (
                                <span className="inline-block mt-2 bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">
                                  {comp.technology}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Functional Requirements */}
                  <CollapsibleCard title="Functional Requirements Compliance" icon={Target} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">ID</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Description</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Priority</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Module</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(proposal.functional_requirements || []).slice(0, 10).map((req, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 text-xs font-mono text-gray-600">{req.requirement}</td>
                                <td className="px-3 py-2 text-xs text-gray-700">{req.description}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    req.priority === 'High' ? 'bg-red-100 text-red-700' : 
                                    req.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                  }`}>{req.priority}</span>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">{req.module}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Technical Specifications */}
                  <CollapsibleCard title="Technical Specifications" icon={Settings} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      {proposal.technical_specifications?.technology_stack && (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                          {Object.entries(proposal.technical_specifications.technology_stack).map(([category, techs]) => (
                            <div key={category} className="bg-gray-50 rounded-lg p-3">
                              <h5 className="font-semibold text-gray-700 text-xs uppercase mb-2">{category}</h5>
                              <div className="flex flex-wrap gap-1">
                                {(techs || []).map((tech, idx) => (
                                  <span key={idx} className="bg-white text-gray-600 text-xs px-2 py-0.5 rounded border">{tech}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {proposal.technical_specifications?.security?.length > 0 && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h5 className="font-semibold text-blue-800 text-sm mb-2">Security Measures</h5>
                          <div className="flex flex-wrap gap-2">
                            {proposal.technical_specifications.security.map((sec, idx) => (
                              <span key={idx} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">{sec}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Implementation Methodology */}
                  <CollapsibleCard title="Implementation Methodology and Approach" icon={Wrench} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{proposal.implementation_methodology?.approach}</p>
                      {proposal.implementation_methodology?.phases?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {proposal.implementation_methodology.phases.map((phase, idx) => (
                            <span key={idx} className="bg-emerald-50 text-emerald-700 text-xs px-3 py-1.5 rounded-full border border-emerald-200">
                              {phase}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Project Plan and Timeline */}
                  <CollapsibleCard title="Project Plan and Timeline" icon={Calendar} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="space-y-4">
                        {(proposal.project_plan || []).map((phase, idx) => (
                          <div key={idx} className="border-l-4 border-emerald-500 bg-gray-50 rounded-r-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900 text-sm">{phase.phase}</h4>
                              <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded">{phase.duration}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              <div>
                                <p className="font-medium text-gray-600 mb-1">Activities:</p>
                                <ul className="space-y-0.5">
                                  {(phase.activities || []).map((act, i) => (
                                    <li key={i} className="text-gray-500">• {act}</li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <p className="font-medium text-gray-600 mb-1">Deliverables:</p>
                                <ul className="space-y-0.5">
                                  {(phase.deliverables || []).map((del, i) => (
                                    <li key={i} className="text-gray-500">• {del}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Project Management */}
                  <CollapsibleCard title="Project Management Approach" icon={Users} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{proposal.project_management?.approach}</p>
                      {proposal.project_management?.governance && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-gray-500 mb-1">Steering Committee</p>
                            <p className="text-sm text-gray-700">{proposal.project_management.governance.steering_committee}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-gray-500 mb-1">Review Frequency</p>
                            <p className="text-sm text-gray-700">{proposal.project_management.governance.review_frequency}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-3">
                            <p className="text-xs font-semibold text-gray-500 mb-1">Escalation Path</p>
                            <p className="text-sm text-gray-700">{proposal.project_management.governance.escalation_path}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Risk Assessment */}
                  <CollapsibleCard title="Risk Assessment and Mitigation Strategy" icon={AlertTriangle} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Risk</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Probability</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Impact</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Mitigation</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(proposal.risk_assessment || []).map((risk, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 text-xs text-gray-700">{risk.risk}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    risk.probability === 'High' ? 'bg-red-100 text-red-700' : 
                                    risk.probability === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                  }`}>{risk.probability}</span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded ${
                                    risk.impact === 'High' ? 'bg-red-100 text-red-700' : 
                                    risk.impact === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                                  }`}>{risk.impact}</span>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">{risk.mitigation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* QA Testing Plan */}
                  <CollapsibleCard title="Quality Assurance and Testing Plan" icon={CheckCircle2} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{proposal.qa_testing_plan?.approach}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h5 className="font-semibold text-blue-800 text-sm mb-2">Types of Testing</h5>
                          <div className="flex flex-wrap gap-2">
                            {(proposal.qa_testing_plan?.types_of_testing || []).map((type, idx) => (
                              <span key={idx} className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded">{type}</span>
                            ))}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-700 text-sm mb-2">Tools</h5>
                          <div className="flex flex-wrap gap-2">
                            {(proposal.qa_testing_plan?.tools || []).map((tool, idx) => (
                              <span key={idx} className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded">{tool}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Security and Compliance */}
                  <CollapsibleCard title="Security and Compliance Measures" icon={Globe} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-red-50 rounded-lg p-4">
                          <h5 className="font-semibold text-red-800 text-sm mb-2">Security Measures</h5>
                          <ul className="space-y-1">
                            {(proposal.security_compliance?.security_measures || []).map((measure, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" />
                                {measure}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <h5 className="font-semibold text-green-800 text-sm mb-2">Compliance Standards</h5>
                          <div className="flex flex-wrap gap-2">
                            {(proposal.security_compliance?.compliance_standards || []).map((std, idx) => (
                              <span key={idx} className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded font-medium">{std}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Staffing Plan */}
                  <CollapsibleCard title="Staffing Plan and Key Personnel" icon={Users} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(proposal.staffing_plan || []).map((staff, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-semibold text-gray-900 text-sm">{staff.role}</h5>
                              {staff.count && <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded">{staff.count}</span>}
                            </div>
                            <p className="text-xs text-gray-500 mb-2">{staff.experience}</p>
                            <ul className="space-y-0.5">
                              {(staff.responsibilities || []).slice(0, 3).map((resp, i) => (
                                <li key={i} className="text-xs text-gray-600">• {resp}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Roles and Responsibilities */}
                  <CollapsibleCard title="Roles and Responsibilities" icon={UserCheck} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="space-y-3">
                        {(proposal.roles_responsibilities || []).map((role, idx) => (
                          <div key={idx} className="border-l-4 border-blue-500 bg-blue-50 rounded-r-lg p-3">
                            <div className="flex items-center justify-between mb-1">
                              <h5 className="font-semibold text-gray-900 text-sm">{role.role}</h5>
                              {role.reporting_to && <span className="text-xs text-gray-500">Reports to: {role.reporting_to}</span>}
                            </div>
                            <ul className="space-y-0.5">
                              {(role.responsibilities || []).map((resp, i) => (
                                <li key={i} className="text-xs text-gray-600">• {resp}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Assumptions and Dependencies */}
                  <CollapsibleCard title="Assumptions and Dependencies" icon={FileText} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h5 className="font-semibold text-blue-800 text-sm mb-2">Assumptions</h5>
                          <ul className="space-y-1">
                            {(proposal.assumptions_dependencies?.assumptions || []).map((item, idx) => (
                              <li key={idx} className="text-xs text-gray-600">• {item}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-4">
                          <h5 className="font-semibold text-amber-800 text-sm mb-2">Dependencies</h5>
                          <ul className="space-y-1">
                            {(proposal.assumptions_dependencies?.dependencies || []).map((item, idx) => (
                              <li key={idx} className="text-xs text-gray-600">• {item}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-gray-100 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-700 text-sm mb-2">Constraints</h5>
                          <ul className="space-y-1">
                            {(proposal.assumptions_dependencies?.constraints || []).map((item, idx) => (
                              <li key={idx} className="text-xs text-gray-600">• {item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Cost Summary */}
                  <CollapsibleCard title="Cost Summary" icon={DollarSign} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      {proposal.cost_summary?.total_cost && (
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white text-center">
                          <p className="text-sm text-emerald-100 mb-1">Total Investment Required</p>
                          <p className="text-3xl sm:text-4xl font-bold">{proposal.cost_summary.total_cost}</p>
                        </div>
                      )}
                      {proposal.cost_summary?.breakdown?.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {proposal.cost_summary.breakdown.map((item, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                              <h5 className="font-semibold text-gray-900 text-sm">{item.category}</h5>
                              <p className="text-lg font-bold text-emerald-600">{item.amount}</p>
                              <p className="text-xs text-gray-500">{item.percentage}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      {proposal.cost_summary?.payment_terms && (
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h5 className="font-semibold text-blue-800 text-sm mb-1">Payment Terms</h5>
                          <p className="text-sm text-gray-700">{proposal.cost_summary.payment_terms}</p>
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Cost Benefit Analysis */}
                  <CollapsibleCard title="Cost Benefit Analysis" icon={TrendingUp} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">Total Investment</p>
                          <p className="text-lg font-bold text-red-600">{proposal.cost_benefit_analysis?.total_investment}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">3-Year Benefits</p>
                          <p className="text-lg font-bold text-green-600">{proposal.cost_benefit_analysis?.three_year_benefits}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">ROI</p>
                          <p className="text-lg font-bold text-purple-600">{proposal.cost_benefit_analysis?.roi_percentage}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">Payback Period</p>
                          <p className="text-lg font-bold text-blue-600">{proposal.cost_benefit_analysis?.payback_period}</p>
                        </div>
                      </div>
                      {proposal.cost_benefit_analysis?.key_value_drivers?.length > 0 && (
                        <div className="bg-emerald-50 rounded-lg p-4">
                          <h5 className="font-semibold text-emerald-800 text-sm mb-2">Key Value Drivers</h5>
                          <ul className="space-y-1">
                            {proposal.cost_benefit_analysis.key_value_drivers.map((driver, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                <TrendingUp className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                {driver}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* References and Case Studies */}
                  <CollapsibleCard title="Relevant References and Case Studies" icon={Building2} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      {!proposal.references_case_studies?.length && (
                        <p className="text-sm text-gray-600" data-testid="proposal-references-unavailable">No verified case studies were available in the source assessment.</p>
                      )}
                      <div className="space-y-4">
                        {(proposal.references_case_studies || []).map((cs, idx) => (
                          <div key={idx} className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">
                                {cs.company?.charAt(0) || 'C'}
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-900">{cs.company}</h5>
                                <p className="text-xs text-orange-600">{cs.industry}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                              <div>
                                <p className="font-semibold text-red-600 mb-1">Challenge</p>
                                <p className="text-gray-600">{cs.challenge}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-blue-600 mb-1">Solution</p>
                                <p className="text-gray-600">{cs.solution}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-green-600 mb-1">Results</p>
                                <ul className="space-y-0.5">
                                  {(Array.isArray(cs.results) ? cs.results : [cs.results]).map((result, i) => (
                                    <li key={i} className="text-gray-600">• {result}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>
                </>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Proposal Generated Yet</h3>
                  <p className="text-gray-500 text-sm mb-4">Click below to generate a comprehensive high-level proposal based on your analysis.</p>
                  <Button onClick={() => fetchOrGenerateProposal()} data-testid="proposal-generate-button" className="bg-emerald-600 hover:bg-emerald-700">
                    Generate Proposal
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Agentic AI Consultant Tab */}
          {COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'agentic' && (
            <div className="space-y-4 sm:space-y-6">
              {/* Header */}
              <div className="bg-gradient-to-br from-cyan-600 via-blue-600 to-indigo-700 rounded-xl shadow-lg p-6 sm:p-8 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Bot className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold">AI Consultant</h2>
                    <p className="text-cyan-100 text-sm">Generate tailored proposals based on your specific concerns</p>
                  </div>
                </div>
                <p className="text-sm text-cyan-100 leading-relaxed">
                  Describe your areas of concern, challenges, or improvement goals. You can also upload supporting documents 
                  (PDF, Word, Excel) for more context. Our AI will generate a comprehensive technical proposal tailored to your needs.
                </p>
              </div>

              {/* Input Section */}
              <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                <div className="space-y-4">
                  {/* Prompt Input */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Sparkles className="w-4 h-4 inline mr-2 text-cyan-500" />
                      Describe Your Areas of Concern or Improvement
                    </label>
                    <textarea
                      value={agenticPrompt}
                      onChange={(e) => setAgenticPrompt(e.target.value)}
                      placeholder="Example: We need to improve our supply chain efficiency, reduce operational costs by 20%, and implement better inventory management. Our current system is outdated and causing delays..."
                      className="w-full h-32 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm resize-none"
                      disabled={agenticLoading}
                    />
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      <Paperclip className="w-4 h-4 inline mr-2 text-gray-500" />
                      Upload Supporting Document (Optional)
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.txt,.csv,.xls,.xlsx"
                        className="hidden"
                        disabled={agenticLoading}
                      />
                      <Button
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={agenticLoading}
                        className="text-sm"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Choose File
                      </Button>
                      {agenticFile && (
                        <div className="flex items-center gap-2 bg-cyan-50 px-3 py-1.5 rounded-lg">
                          <FileText className="w-4 h-4 text-cyan-600" />
                          <span className="text-sm text-cyan-700">{agenticFile.name}</span>
                          <button
                            onClick={() => {
                              setAgenticFile(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                            className="text-cyan-600 hover:text-cyan-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Supported: PDF, Word, Excel, CSV, TXT (max 20MB)</p>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end">
                    <Button
                      onClick={handleAgenticSubmit}
                      disabled={agenticLoading || !agenticPrompt.trim()}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white px-6"
                    >
                      {agenticLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating Proposal...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Generate Proposal
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Loading State */}
              {agenticLoading && (
                <div className="bg-white rounded-xl shadow-sm p-8 sm:p-12 text-center">
                  <div className="relative">
                    <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Bot className="w-8 h-8 text-cyan-600 animate-pulse" />
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-20 h-20 border-4 border-cyan-200 border-t-cyan-600 rounded-full animate-spin"></div>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4">Analyzing Your Requirements</h3>
                  <p className="text-gray-500 text-sm">Our AI is creating a comprehensive proposal based on your input...</p>
                  <p className="text-gray-400 text-xs mt-2">This may take up to 2 minutes</p>
                </div>
              )}

              {/* Error State */}
              {agenticError && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-red-700 text-sm font-medium">Error</p>
                    <p className="text-red-600 text-sm">{agenticError}</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAgenticError('')}
                    className="ml-auto border-red-200 text-red-600 hover:bg-red-100"
                  >
                    Dismiss
                  </Button>
                </div>
              )}

              {/* Generated Proposal Display */}
              {agenticProposal && !agenticLoading && (
                <>
                  {/* Executive Summary */}
                  <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl shadow-sm p-6 sm:p-8 text-white">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-5 h-5" />
                      <h2 className="text-xl sm:text-2xl font-bold">Generated Proposal</h2>
                    </div>
                    <h3 className="text-lg font-semibold mb-2 text-purple-200">Executive Summary</h3>
                    <p className="text-sm sm:text-base text-purple-100 leading-relaxed whitespace-pre-line">
                      {agenticProposal.executive_summary}
                    </p>
                    {agenticProposal.user_prompt && (
                      <div className="mt-4 pt-4 border-t border-purple-400/30">
                        <p className="text-xs text-purple-300">Based on your input:</p>
                        <p className="text-sm text-purple-200 italic mt-1">&quot;{agenticProposal.user_prompt.substring(0, 200)}{agenticProposal.user_prompt.length > 200 ? '...' : ''}&quot;</p>
                      </div>
                    )}
                  </div>

                  {/* Areas of Concern */}
                  <CollapsibleCard title="Areas of Concern" icon={AlertTriangle} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(agenticProposal.areas_of_concern || []).map((concern, idx) => (
                          <div key={idx} className="bg-red-50 border border-red-100 rounded-lg p-4">
                            <h4 className="font-semibold text-red-800 text-sm mb-2">{concern.area}</h4>
                            <p className="text-xs text-gray-600 mb-2">{concern.description}</p>
                            {concern.priority && (
                              <span className={`inline-block text-xs px-2 py-0.5 rounded ${concern.priority === 'High' ? 'bg-red-100 text-red-700' : concern.priority === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                                {concern.priority} Priority
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Proposed Technical Solution */}
                  <CollapsibleCard title="Proposed Technical Solution" icon={Cpu} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{agenticProposal.proposed_technical_solution?.overview}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-800 text-sm mb-2">Key Components</h4>
                          <ul className="space-y-1">
                            {(agenticProposal.proposed_technical_solution?.key_components || []).map((comp, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                <CheckCircle2 className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                {comp}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4">
                          <h4 className="font-semibold text-green-800 text-sm mb-2">Benefits</h4>
                          <ul className="space-y-1">
                            {(agenticProposal.proposed_technical_solution?.benefits || []).map((benefit, idx) => (
                              <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                                <TrendingUp className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                {benefit}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* System Architecture */}
                  <CollapsibleCard title="System Architecture and Design" icon={Globe}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <p className="text-gray-700 text-sm">{agenticProposal.system_architecture?.description}</p>
                      {agenticProposal.system_architecture?.components?.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {agenticProposal.system_architecture.components.map((comp, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                              <h5 className="font-semibold text-gray-900 text-sm">{comp.name}</h5>
                              <p className="text-xs text-gray-500 mt-1">{comp.description}</p>
                              {comp.technology && <span className="inline-block mt-2 bg-purple-100 text-purple-700 text-xs px-2 py-0.5 rounded">{comp.technology}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Technical Specifications */}
                  <CollapsibleCard title="Technical Specifications" icon={Settings}>
                    <div className="p-4 sm:p-6 space-y-4">
                      {agenticProposal.technical_specifications?.technology_stack && (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                          {Object.entries(agenticProposal.technical_specifications.technology_stack).map(([category, techs]) => (
                            <div key={category} className="bg-gray-50 rounded-lg p-3">
                              <h5 className="font-semibold text-gray-700 text-xs uppercase mb-2">{category}</h5>
                              <div className="flex flex-wrap gap-1">
                                {(techs || []).map((tech, idx) => (
                                  <span key={idx} className="bg-white text-gray-600 text-xs px-2 py-0.5 rounded border">{tech}</span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Project Plan */}
                  <CollapsibleCard title="Project Plan and Timeline" icon={Calendar}>
                    <div className="p-4 sm:p-6">
                      <div className="space-y-4">
                        {(agenticProposal.project_plan || []).map((phase, idx) => (
                          <div key={idx} className="border-l-4 border-emerald-500 bg-gray-50 rounded-r-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-semibold text-gray-900 text-sm">{phase.phase}</h4>
                              <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded">{phase.duration}</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                              <div>
                                <p className="font-medium text-gray-600 mb-1">Activities:</p>
                                <ul className="space-y-0.5">
                                  {(phase.activities || []).map((act, i) => <li key={i} className="text-gray-500">• {act}</li>)}
                                </ul>
                              </div>
                              <div>
                                <p className="font-medium text-gray-600 mb-1">Deliverables:</p>
                                <ul className="space-y-0.5">
                                  {(phase.deliverables || []).map((del, i) => <li key={i} className="text-gray-500">• {del}</li>)}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Risk Assessment */}
                  <CollapsibleCard title="Risk Assessment and Mitigation" icon={AlertTriangle}>
                    <div className="p-4 sm:p-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Risk</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Probability</th>
                              <th className="px-3 py-2 text-center text-xs font-semibold text-gray-500">Impact</th>
                              <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Mitigation</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {(agenticProposal.risk_assessment || []).map((risk, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 text-xs text-gray-700">{risk.risk}</td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded ${risk.probability === 'High' ? 'bg-red-100 text-red-700' : risk.probability === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{risk.probability}</span>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span className={`text-xs px-2 py-0.5 rounded ${risk.impact === 'High' ? 'bg-red-100 text-red-700' : risk.impact === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{risk.impact}</span>
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">{risk.mitigation}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* Cost Summary */}
                  <CollapsibleCard title="Cost Summary" icon={DollarSign} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      {agenticProposal.cost_summary?.total_cost && (
                        <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl p-6 text-white text-center">
                          <p className="text-sm text-emerald-100 mb-1">Total Investment Required</p>
                          <p className="text-3xl sm:text-4xl font-bold">{agenticProposal.cost_summary.total_cost}</p>
                        </div>
                      )}
                      {agenticProposal.cost_summary?.breakdown?.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                          {agenticProposal.cost_summary.breakdown.map((item, idx) => (
                            <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                              <h5 className="font-semibold text-gray-900 text-sm">{item.category}</h5>
                              <p className="text-lg font-bold text-emerald-600">{item.amount}</p>
                              <p className="text-xs text-gray-500">{item.percentage}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CollapsibleCard>

                  {/* Cost Benefit Analysis */}
                  <CollapsibleCard title="Cost Benefit Analysis" icon={TrendingUp} defaultOpen={true}>
                    <div className="p-4 sm:p-6 space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">Total Investment</p>
                          <p className="text-lg font-bold text-red-600">{agenticProposal.cost_benefit_analysis?.total_investment}</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">3-Year Benefits</p>
                          <p className="text-lg font-bold text-green-600">{agenticProposal.cost_benefit_analysis?.three_year_benefits}</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">ROI</p>
                          <p className="text-lg font-bold text-purple-600">{agenticProposal.cost_benefit_analysis?.roi_percentage}</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <p className="text-xs text-gray-500 mb-1">Payback Period</p>
                          <p className="text-lg font-bold text-blue-600">{agenticProposal.cost_benefit_analysis?.payback_period}</p>
                        </div>
                      </div>
                    </div>
                  </CollapsibleCard>

                  {/* References and Case Studies */}
                  <CollapsibleCard title="Relevant References and Case Studies" icon={Building2} defaultOpen={true}>
                    <div className="p-4 sm:p-6">
                      <div className="space-y-4">
                        {(agenticProposal.references_case_studies || []).map((cs, idx) => (
                          <div key={idx} className="bg-orange-50 rounded-lg p-4 border border-orange-100">
                            <div className="flex items-center gap-3 mb-3">
                              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center text-white font-bold">
                                {cs.company?.charAt(0) || 'C'}
                              </div>
                              <div>
                                <h5 className="font-semibold text-gray-900">{cs.company}</h5>
                                <p className="text-xs text-orange-600">{cs.industry}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                              <div>
                                <p className="font-semibold text-red-600 mb-1">Challenge</p>
                                <p className="text-gray-600">{cs.challenge}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-blue-600 mb-1">Solution</p>
                                <p className="text-gray-600">{cs.solution}</p>
                              </div>
                              <div>
                                <p className="font-semibold text-green-600 mb-1">Results</p>
                                <ul className="space-y-0.5">
                                  {(Array.isArray(cs.results) ? cs.results : [cs.results]).map((result, i) => (
                                    <li key={i} className="text-gray-600">• {result}</li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CollapsibleCard>
                </>
              )}

              {/* History Section */}
              {agenticHistory.length > 0 && !agenticLoading && !agenticProposal && (
                <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-cyan-600" />
                    Previous Proposals
                  </h3>
                  <div className="space-y-3">
                    {agenticHistory.slice(0, 5).map((hist, idx) => (
                      <button
                        key={hist.id || idx}
                        onClick={() => setAgenticProposal(hist)}
                        className="w-full text-left bg-gray-50 hover:bg-cyan-50 rounded-lg p-4 border border-gray-100 hover:border-cyan-200 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {hist.user_prompt?.substring(0, 60)}{hist.user_prompt?.length > 60 ? '...' : ''}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(hist.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        {hist.document_name && (
                          <div className="flex items-center gap-1 text-xs text-cyan-600">
                            <Paperclip className="w-3 h-3" />
                            {hist.document_name}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty State */}
              {!agenticLoading && !agenticProposal && agenticHistory.length === 0 && (
                <div className="bg-gray-50 rounded-xl p-8 text-center border-2 border-dashed border-gray-200">
                  <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">No Proposals Yet</h3>
                  <p className="text-gray-500 text-sm">
                    Enter your areas of concern above and click &quot;Generate Proposal&quot; to create your first AI-powered proposal.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* AGI Insights Tab — visible only after secret-code unlock (hasFullAccess) */}
          {COMPANY_DASHBOARD_TOOLS_ENABLED && activeTab === 'agi' && (
            <div className="space-y-6">
              <AGIInsightsTab
                assessmentType="Company Performance"
                assessmentId={analysis && (analysis.id || analysis.analysis_id)}
              />
            </div>
          )}
          <ReportSecurityNote />
        </main>
      </div>
    </div>
  );
};

export default DashboardPage;
